import { useEffect, useRef } from 'react';
import { DD } from '@tcc/analytics';

const APP_ROUTER_LOAD_VITAL = 'app_router_load';

/**
 * Records a Datadog RUM duration vital measuring how long AppRouter shows its
 * loading spinner — Stage 4 of startup: the i18n boot **and** the logged-in-user
 * fetch together (whichever finishes last). Timing starts while `isRouterLoading`
 * is true and stops once it flips to false. All RUM calls are gated on an
 * available instance, and the vital is recorded at most once.
 *
 * Note: the `app_boot` vital is a nested sub-measurement (i18n boot only); this
 * vital covers the whole gate (boot + user), so it can exceed `app_boot`.
 */
export const useAppRouterLoadVital = (isRouterLoading: boolean): void => {
  const hasStartedRef = useRef(false);
  const hasRecordedRef = useRef(false);

  // Start timing when the AppRouter loading spinner is shown.
  useEffect(() => {
    if (hasRecordedRef.current || hasStartedRef.current || !isRouterLoading) return;
    DD.startDurationVital(APP_ROUTER_LOAD_VITAL, {
      context: { gate: 'boot_and_user' },
      description: 'Time the AppRouter spinner is shown while i18n boot and the logged-in user resolve',
    });
    hasStartedRef.current = true;
  }, [isRouterLoading]);

  // Stop timing once boot and user resolve and AppRouter renders its routes.
  useEffect(() => {
    if (hasRecordedRef.current || isRouterLoading || !hasStartedRef.current) return;
    DD.stopDurationVital(APP_ROUTER_LOAD_VITAL);
    hasRecordedRef.current = true;
  }, [isRouterLoading]);
};
